import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    List,
    ListOrdered,
    Heading1,
    Heading2,
    Heading3,
    Link as LinkIcon,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Quote,
    Undo2,
    Redo2,
    Code,
    Variable,
    Search,
    User,
    BarChart3,
} from 'lucide-react'

export interface RichTextEditorProps {
    value: string
    onChange: (html: string) => void
    placeholder?: string
    minHeight?: number
    variables?: Array<{ key: string; label: string }>
    disabled?: boolean
}

const TOOLBAR_BTN =
    'inline-flex h-8 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed'
const TOOLBAR_BTN_ACTIVE = 'bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-700'

function ToolbarButton({
    onClick,
    active,
    disabled,
    children,
    title,
}: {
    onClick: () => void
    active?: boolean
    disabled?: boolean
    children: React.ReactNode
    title: string
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`${TOOLBAR_BTN} ${active ? TOOLBAR_BTN_ACTIVE : ''}`}
        >
            {children}
        </button>
    )
}

/*
 * VariablePicker — command-palette style dropdown. Groups variables by the
 * `user.` / `digest.` prefix (with a "Custom" bucket for anything else),
 * filters live on type, and supports arrow-key navigation. Each row ends in
 * a mono code chip showing the exact marker that will be inserted, so the
 * admin knows what they're about to commit to.
 */
type GroupAccent = 'sky' | 'emerald' | 'amber'

const ACCENT_STYLES: Record<
    GroupAccent,
    { bg: string; fg: string; ring: string }
> = {
    sky: { bg: 'bg-sky-100', fg: 'text-sky-700', ring: 'ring-sky-200' },
    emerald: { bg: 'bg-emerald-100', fg: 'text-emerald-700', ring: 'ring-emerald-200' },
    amber: { bg: 'bg-amber-100', fg: 'text-amber-700', ring: 'ring-amber-200' },
}

function VariablePicker({
    variables,
    onPick,
}: {
    variables: Array<{ key: string; label: string }>
    onPick: (key: string) => void
}) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [focused, setFocused] = useState(0)
    const ref = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const rowRefs = useRef<Array<HTMLButtonElement | null>>([])

    const groups = useMemo(() => {
        const q = query.trim().toLowerCase()
        const match = (v: { key: string; label: string }) =>
            !q || v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q)

        const buckets = [
            {
                title: 'User data',
                hint: 'Auto-bound to the real recipient',
                icon: User,
                accent: 'sky' as GroupAccent,
                items: variables.filter(v => v.key.startsWith('user.') && match(v)),
            },
            {
                title: 'Digest metrics',
                hint: 'Aggregate counts for the current user',
                icon: BarChart3,
                accent: 'emerald' as GroupAccent,
                items: variables.filter(v => v.key.startsWith('digest.') && match(v)),
            },
            {
                title: 'Custom',
                hint: 'Static slot values filled in by the admin',
                icon: Variable,
                accent: 'amber' as GroupAccent,
                items: variables.filter(
                    v =>
                        !v.key.startsWith('user.') &&
                        !v.key.startsWith('digest.') &&
                        match(v),
                ),
            },
        ]
        return buckets.filter(g => g.items.length > 0)
    }, [variables, query])

    const flat = useMemo(
        () =>
            groups.flatMap(g =>
                g.items.map(item => ({ ...item, accent: g.accent })),
            ),
        [groups],
    )

    useEffect(() => {
        setFocused(0)
    }, [query, open])

    useEffect(() => {
        if (!open) return
        // Defer so the input exists in the DOM before we focus it.
        const t = setTimeout(() => inputRef.current?.focus(), 0)
        return () => clearTimeout(t)
    }, [open])

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
                setQuery('')
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    useEffect(() => {
        rowRefs.current[focused]?.scrollIntoView({ block: 'nearest' })
    }, [focused])

    function commit(key: string) {
        onPick(`{{${key}}}`)
        setOpen(false)
        setQuery('')
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setFocused(f => Math.min(Math.max(flat.length - 1, 0), f + 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setFocused(f => Math.max(0, f - 1))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            const pick = flat[focused]
            if (pick) commit(pick.key)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            setOpen(false)
            setQuery('')
        }
    }

    // Running index used to track which row is "focused" during render. Reset
    // on every render, incremented in-line per row — mirrors the `flat` array.
    let idx = 0

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                title="Insert variable"
                className={`inline-flex h-8 items-center gap-1.5 rounded px-2 text-xs font-medium transition-colors ${
                    open
                        ? 'bg-gray-900 text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
            >
                <Variable className="h-3.5 w-3.5" />
                Insert variable
            </button>
            {open && (
                <div className="absolute left-0 top-9 z-30 w-[400px] origin-top-left animate-[fade-in_140ms_ease-out] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5">
                    {/* Search bar */}
                    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5">
                        <Search className="h-3.5 w-3.5 text-gray-400" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Search variables…"
                            className="flex-1 border-0 bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none"
                        />
                        <div className="flex items-center gap-1 text-[10px] text-gray-400">
                            <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono">
                                ↑↓
                            </kbd>
                            <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono">
                                ↵
                            </kbd>
                        </div>
                    </div>

                    {/* Groups */}
                    <div className="max-h-[340px] overflow-y-auto py-1">
                        {groups.length === 0 && (
                            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                                <Search className="h-5 w-5 text-gray-300" />
                                <span className="text-xs text-gray-500">
                                    No variables match{' '}
                                    <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">
                                        {query}
                                    </code>
                                </span>
                            </div>
                        )}
                        {groups.map(g => {
                            const Icon = g.icon
                            const style = ACCENT_STYLES[g.accent]
                            return (
                                <div key={g.title} className="mb-1 last:mb-0">
                                    <div className="flex items-center gap-2 px-3 pb-1 pt-2">
                                        <span
                                            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded ring-1 ${style.bg} ${style.fg} ${style.ring}`}
                                        >
                                            <Icon className="h-2.5 w-2.5" />
                                        </span>
                                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                                            {g.title}
                                        </span>
                                        <span className="truncate text-[10px] text-gray-400">
                                            · {g.hint}
                                        </span>
                                    </div>
                                    {g.items.map(v => {
                                        const myIdx = idx++
                                        const isFocused = myIdx === focused
                                        return (
                                            <button
                                                key={v.key}
                                                type="button"
                                                ref={el => {
                                                    rowRefs.current[myIdx] = el
                                                }}
                                                onMouseEnter={() => setFocused(myIdx)}
                                                onClick={() => commit(v.key)}
                                                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                                                    isFocused
                                                        ? 'bg-gray-50'
                                                        : 'hover:bg-gray-50/60'
                                                }`}
                                            >
                                                <span
                                                    className={`truncate text-sm ${
                                                        isFocused
                                                            ? 'font-medium text-gray-900'
                                                            : 'text-gray-800'
                                                    }`}
                                                >
                                                    {v.label}
                                                </span>
                                                <code
                                                    className={`shrink-0 rounded-md px-2 py-0.5 font-mono text-[10.5px] transition-all ${
                                                        isFocused
                                                            ? `${style.bg} ${style.fg} ring-1 ${style.ring}`
                                                            : 'bg-gray-50 text-gray-500 ring-1 ring-gray-200/70'
                                                    }`}
                                                >
                                                    {`{{${v.key}}}`}
                                                </code>
                                            </button>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>

                    {/* Footer hint */}
                    <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-3 py-2 text-[10px] text-gray-500">
                        <span>
                            {flat.length} variable{flat.length === 1 ? '' : 's'}
                        </span>
                        <span className="font-mono">
                            {flat[focused]?.key ?? '—'}
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}

function Toolbar({
    editor,
    variables,
}: {
    editor: Editor
    variables?: Array<{ key: string; label: string }>
}) {
    const setLink = () => {
        const previous = editor.getAttributes('link').href as string | undefined
        const url = window.prompt('Link URL', previous ?? 'https://')
        if (url === null) return
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            return
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }

    const insertVariable = (text: string) => {
        editor.chain().focus().insertContent(text).run()
    }

    return (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
            <ToolbarButton
                title="Heading 1"
                active={editor.isActive('heading', { level: 1 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            >
                <Heading1 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Heading 2"
                active={editor.isActive('heading', { level: 2 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            >
                <Heading2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Heading 3"
                active={editor.isActive('heading', { level: 3 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
                <Heading3 className="h-4 w-4" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-gray-300" />
            <ToolbarButton
                title="Bold"
                active={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
            >
                <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Italic"
                active={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            >
                <Italic className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Underline"
                active={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
                <UnderlineIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Inline code"
                active={editor.isActive('code')}
                onClick={() => editor.chain().focus().toggleCode().run()}
            >
                <Code className="h-4 w-4" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-gray-300" />
            <ToolbarButton
                title="Bullet list"
                active={editor.isActive('bulletList')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
                <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Numbered list"
                active={editor.isActive('orderedList')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
                <ListOrdered className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Quote"
                active={editor.isActive('blockquote')}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
                <Quote className="h-4 w-4" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-gray-300" />
            <ToolbarButton
                title="Align left"
                active={editor.isActive({ textAlign: 'left' })}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
            >
                <AlignLeft className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Align center"
                active={editor.isActive({ textAlign: 'center' })}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
            >
                <AlignCenter className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Align right"
                active={editor.isActive({ textAlign: 'right' })}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
            >
                <AlignRight className="h-4 w-4" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-gray-300" />
            <ToolbarButton title="Link" active={editor.isActive('link')} onClick={setLink}>
                <LinkIcon className="h-4 w-4" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-gray-300" />
            <ToolbarButton
                title="Undo"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
            >
                <Undo2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
                title="Redo"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
            >
                <Redo2 className="h-4 w-4" />
            </ToolbarButton>
            {variables && variables.length > 0 && (
                <>
                    <span className="mx-1 h-5 w-px bg-gray-300" />
                    <VariablePicker variables={variables} onPick={insertVariable} />
                </>
            )}
        </div>
    )
}

export function RichTextEditor({
    value,
    onChange,
    placeholder,
    minHeight = 240,
    variables,
    disabled,
}: RichTextEditorProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                    HTMLAttributes: { class: 'font-semibold text-gray-900' },
                },
                bulletList: {
                    HTMLAttributes: { class: 'list-disc pl-6 my-2 space-y-1' },
                },
                orderedList: {
                    HTMLAttributes: { class: 'list-decimal pl-6 my-2 space-y-1' },
                },
                listItem: {
                    HTMLAttributes: { class: 'pl-1' },
                },
                blockquote: {
                    HTMLAttributes: {
                        class: 'border-l-4 border-gray-300 pl-4 italic text-gray-700 my-3',
                    },
                },
                code: {
                    HTMLAttributes: {
                        class: 'rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.9em] text-gray-800',
                    },
                },
                codeBlock: {
                    HTMLAttributes: {
                        class: 'rounded bg-gray-900 text-gray-100 p-3 font-mono text-xs my-3 overflow-x-auto',
                    },
                },
                paragraph: {
                    HTMLAttributes: { class: 'my-2' },
                },
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { class: 'text-blue-600 underline' },
            }),
            Placeholder.configure({
                placeholder: placeholder ?? 'Write your message…',
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        content: value || '',
        editable: !disabled,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML()
            onChange(html === '<p></p>' ? '' : html)
        },
        editorProps: {
            attributes: {
                class: 'tiptap-editor max-w-none focus:outline-none px-4 py-3 text-gray-900 leading-relaxed',
                style: `min-height: ${minHeight}px`,
            },
        },
    })

    // Keep external value -> editor in sync (e.g., when switching templates)
    useEffect(() => {
        if (!editor) return
        const current = editor.getHTML()
        const incoming = value || ''
        if (current === incoming) return
        if (current === '<p></p>' && !incoming) return
        editor.commands.setContent(incoming, false)
    }, [value, editor])

    if (!editor) return null

    return (
        <div className="overflow-hidden rounded-md border border-gray-300 bg-white">
            <Toolbar editor={editor} variables={variables} />
            <EditorContent editor={editor} />
        </div>
    )
}
